/**
 * The repo's single HTTP client: config for the canonical axios factory, nothing more.
 * All interceptor behaviour lives in `./apiAxiosInstance` — see
 * `software-architect-ai/http-client-strategy.md` ("extend the factory, never fork it").
 *
 * Shape of this surface (genesis handoff §4):
 *  - Same-origin: `baseUrl: ''`. The API is served on the brand host, NOT on a separate auth host.
 *  - `withCredentials: true` always (the HttpOnly refresh cookie rides on it) +
 *    `X-Request-ID` per call — both from the factory.
 *  - Bearer on `/api/tenant/**` and `/api/auth/me`.
 *  - CSRF per protected surface: cookie-echo on `/api/auth/**` (read the shared-apex
 *    `journix_csrf` cookie, falling back to `GET /api/auth/csrf`) and store-token,
 *    header-only, on `/api/tenant/**` writes.
 *  - Silent refresh single-in-flight on 401 (and on 403 INVALID_CSRF for a write);
 *    retried once; on failure `onSessionExpired` clears the session and the registered
 *    handler restarts the centralized-login kickoff.
 *  - No tenant context is ever sent: the tenant is derived server-side from the JWT.
 */
import { createApiClient } from './apiAxiosInstance';
import { generateRequestId } from '@/utils/uuid';
import {
  getAccessToken,
  getCsrfToken,
  notifySessionExpired,
  setAccessToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';
import type { ApiResponse } from './types';

export const REFRESH_PATH = '/api/auth/refresh';
export const LOGOUT_PATH = '/api/auth/logout';
export const SESSION_EXCHANGE_PATH = '/api/auth/session/exchange';
export const ME_PATH = '/api/auth/me';
export const AUTH_API_PREFIX = '/api/auth/';
export const TENANT_API_PREFIX = '/api/tenant/';

/** The saas-auth cookie surface: refresh / logout / csrf etc. */
export function isAuthPath(path: string): boolean {
  return path.startsWith(AUTH_API_PREFIX);
}

/** Bearer surfaces: the tenant-scoped API plus the identity probe. */
export function isBearerPath(path: string): boolean {
  return path.startsWith(TENANT_API_PREFIX) || path === ME_PATH;
}

/**
 * Writes that carry `X-CSRF-Token`. The session-establishing exchange is excluded —
 * there is no in-memory token yet at that point (the exchange is what mints one).
 */
export function isCsrfProtectedPath(path: string, method: string): boolean {
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return false;
  return path.startsWith(TENANT_API_PREFIX) || path === REFRESH_PATH || path === LOGOUT_PATH;
}

export const apiClient = createApiClient({
  baseUrl: '',
  generateRequestId,
  tokens: {
    getAccess: getAccessToken,
    setAccess: (token, expiresAt) => setAccessToken(token, expiresAt ?? null),
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: isBearerPath },
  csrf: {
    protected: isCsrfProtectedPath,
    // Auth surface (refresh/logout): cookie-echo — the CSRF token is sourced from the
    // JS-readable journix_csrf cookie when present, falling back to GET /api/auth/csrf
    // (that fallback covers whitelabel apexes, where the cookie isn't shared). This is
    // what makes the cold-boot refresh carry a token, now same-origin.
    // Tenant surface (brand writes): store-token, header-only from the in-memory store
    // (seeded from the refresh/exchange response body), matching saas-admin — never a
    // csrfToken body echo on the full-replace brand PUT.
    mode: (path) => (isAuthPath(path) ? 'cookie-echo' : 'store-token'),
  },
  refresh: { path: REFRESH_PATH },
  onSessionExpired: () => {
    signOut();
    notifySessionExpired();
  },
});

/** Unwrap the `{ success, message, data }` envelope. */
export function unwrap<T>(response: ApiResponse<T>): T {
  return response.data;
}
